import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { friendApi, memberSummaryApi, conversationApi } from '@formosa/shared/api/im';
import '../../styles/chat.css';

function useSummaries(memberIds) {
  const [map, setMap] = useState({});
  useEffect(() => {
    const ids = [...new Set((memberIds || []).filter(Boolean))];
    if (!ids.length) {
      setMap({});
      return;
    }
    let cancelled = false;
    memberSummaryApi
      .batch(ids)
      .then((list) => {
        if (cancelled) return;
        const next = {};
        (list || []).forEach((s) => {
          if (s?.memberId != null) next[s.memberId] = s;
        });
        setMap(next);
      })
      .catch(() => {
        if (!cancelled) setMap({});
      });
    return () => {
      cancelled = true;
    };
  }, [memberIds.join(',')]);
  return map;
}

/** /chat/contacts — friends list + links to requests / search */
export default function ContactsPage() {
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    friendApi
      .listFriends()
      .then((data) => setFriends(Array.isArray(data) ? data : []))
      .catch((e) => setError(e?.message || '載入失敗'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const ids = friends.map((f) => f.memberId);
  const summaries = useSummaries(ids);

  const openChat = async (memberId) => {
    try {
      const conv = await conversationApi.createOrGetDirect(memberId);
      const cid = conv?.conversationId || conv?.id;
      if (cid) navigate(`/chat/${cid}`);
    } catch (e) {
      setError(e?.message || '無法開啟私聊');
    }
  };

  const remove = async (memberId) => {
    if (!window.confirm('確定解除好友？')) return;
    try {
      await friendApi.removeFriend(memberId);
      load();
    } catch (e) {
      setError(e?.message || '解除失敗');
    }
  };

  return (
    <div className="chat-social-page">
      <header className="chat-social-page__header">
        <Link to="/chat" className="chat-link-btn">
          ← 聊天
        </Link>
        <h1>聯絡人</h1>
        <div className="chat-social-page__actions">
          <Link to="/chat/friend-requests">邀請</Link>
          <Link to="/chat/search">搜尋</Link>
          <Link to="/chat/groups">群組</Link>
          <Link to="/chat/blocked">封鎖</Link>
        </div>
      </header>
      {error ? <p className="chat-social-page__error">{error}</p> : null}
      {loading ? <p>載入中…</p> : null}
      <ul className="chat-social-page__list">
        {friends.map((f) => {
          const s = summaries[f.memberId];
          const name = s?.displayName || s?.username || `會員 ${f.memberId}`;
          return (
            <li key={f.memberId}>
              <button type="button" className="chat-social-page__row" onClick={() => openChat(f.memberId)}>
                <span className="chat-social-page__name">{name}</span>
                <span className="chat-social-page__meta">@{s?.username || f.memberId}</span>
              </button>
              <button type="button" className="chat-link-btn" onClick={() => remove(f.memberId)}>
                解除
              </button>
              <Link
                to={`/chat/report?memberId=${encodeURIComponent(f.memberId)}`}
                className="chat-link-btn"
              >
                檢舉
              </Link>
            </li>
          );
        })}
      </ul>
      {!loading && !friends.length ? <p className="chat-social-page__empty">尚無好友，去搜尋加入吧。</p> : null}
    </div>
  );
}
